import { useCallback, useEffect, useState } from 'react'

import { restClient } from '@/Core'

interface ReportFetchState<T> {
    /** Остання отримана відповідь (під час нового запиту — попередня, щоб таблиця не блимала) */
    data: T | null
    /** Параметри, з якими отримано `data` (можуть відрізнятися від поточних, поки йде запит) */
    dataParams: Record<string, string> | null
    loading: boolean
    error: string | null
    reload: () => void
}

/** Зрозуміле повідомлення про помилку запиту звіту */
const errorMessage = (e: any): string => {
    const status = e?.statusCode ?? e?.response?.status
    if (status === 403) return 'Немає доступу до звітів'
    if (status === 400) return 'Некоректні параметри звіту'
    return 'Не вдалося завантажити звіт. Спробуйте ще раз.'
}

/**
 * GET звіту з параметрами: новий запит при зміні url/параметрів, попередній
 * переривається (відповідь застарілого запиту не перезапише свіжу). Кеш restClient
 * вимкнено — звіт завжди актуальний.
 */
export const useReportFetch = <T>(url: string, params: Record<string, string>): ReportFetchState<T> => {
    const [result, setResult] = useState<{ query: string; data: T | null } | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [attempt, setAttempt] = useState(0)

    const query = new URLSearchParams(params).toString()

    useEffect(() => {
        const controller = new AbortController()
        setLoading(true)
        setError(null)
        const request = restClient.get(
            url,
            { searchParams: new URLSearchParams(query), signal: controller.signal },
            { isDisableResponseCache: true }
        ) as unknown as Promise<T> | null

        Promise.resolve(request)
            .then((response) => {
                if (!controller.signal.aborted) setResult({ query, data: response ?? null })
            })
            .catch((e: any) => {
                if (controller.signal.aborted || e?.name === 'AbortError') return
                setError(errorMessage(e))
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false)
            })

        return () => controller.abort()
    }, [url, query, attempt])

    const reload = useCallback(() => setAttempt((value) => value + 1), [])

    return {
        data: result?.data ?? null,
        dataParams: result ? Object.fromEntries(new URLSearchParams(result.query)) : null,
        loading,
        error,
        reload,
    }
}
